import {
  MfaFactor,
  webAuthnRegistrationOptionsGuard,
  type WebAuthnAuthenticationOptions,
  type WebAuthnRegistrationOptions,
} from '@logto/schemas';
import { trySafe } from '@silverhand/essentials';
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import type { RegistrationResponseJSON, AuthenticationResponseJSON } from '@simplewebauthn/types';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { UserMfaFlow } from '@/types';

import useSendMfaPayload from './use-send-mfa-payload';
import useToast from './use-toast';

type WebAuthnOptions = WebAuthnRegistrationOptions | WebAuthnAuthenticationOptions;

const isAuthenticationResponseJSON = (
  responseJson: RegistrationResponseJSON | AuthenticationResponseJSON
): responseJson is AuthenticationResponseJSON => {
  return 'signature' in responseJson.response;
};

/**
 * Guard against browsers (notably Safari on macOS 26) that occasionally accept
 * a WebAuthn create/get call without ever surfacing the authenticator picker
 * nor rejecting the promise. The stock authenticator `timeout` field in the
 * options is only advisory for the platform; if it never fires the caller
 * hangs forever.
 *
 * We race the WebAuthn call with a hard client timeout **and** – critically
 * for Safari – abort the still-pending ceremony via SimpleWebAuthn's shared
 * `WebAuthnAbortService`. Without that abort, `Promise.race` only unblocks
 * our UI while the underlying `navigator.credentials.create()` remains
 * pending inside the browser; subsequent user attempts then inherit that
 * stuck ceremony and either hang again or reject with an opaque error.
 * Calling `cancelCeremony()` rejects the pending browser call with
 * `AbortError`, freeing Safari to start a fresh ceremony on retry.
 */
const webAuthnClientTimeoutMs = 30_000;

class WebAuthnClientTimeoutError extends Error {
  constructor(message = 'WebAuthn client timeout') {
    super(message);
    this.name = 'WebAuthnClientTimeoutError';
  }
}

const withClientTimeout = async <T>(operation: () => Promise<T>): Promise<T> => {
  // A timer handle must be captured by the Promise executor and cleared in
  // `finally`; this local mutation does not escape the operation.
  /* eslint-disable @silverhand/fp/no-let, @silverhand/fp/no-mutation */
  let timerId: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeoutPromise = new Promise<T>((_resolve, reject) => {
      timerId = setTimeout(() => {
        // Actually abort the pending navigator.credentials.* ceremony so the
        // browser (Safari especially) does not keep an invisible sheet open.
        try {
          WebAuthnAbortService.cancelCeremony();
        } catch {
          // Best-effort; ignore secondary failures from the abort service.
        }
        reject(new WebAuthnClientTimeoutError());
      }, webAuthnClientTimeoutMs);
    });
    return await Promise.race([operation(), timeoutPromise]);
  } finally {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
  }
  /* eslint-enable @silverhand/fp/no-let, @silverhand/fp/no-mutation */
};

const useWebAuthnOperation = () => {
  const { t } = useTranslation();
  const { setToast } = useToast();
  const sendMfaPayload = useSendMfaPayload();

  return useCallback(
    /**
     * Note:
     * Due to limitations in the iOS system, user interaction is required for the use of the WebAuthn API.
     * Therefore, we should avoid asynchronous operations before invoking the WebAuthn API or the os may consider the WebAuthn authorization is not initiated by the user.
     * So, we need to prepare the necessary WebAuthn options before calling the WebAuthn API, this is why we don't generate the options in this function.
     */
    async (options: WebAuthnOptions, verificationId: string) => {
      if (!browserSupportsWebAuthn()) {
        setToast(t('mfa.webauthn_not_supported'));
        return;
      }

      const parsedRegistrationOptions = webAuthnRegistrationOptionsGuard.safeParse(options);

      // Ensure any lingering ceremony (e.g. from a previous stuck attempt or
      // a conditional-mediation `credentials.get()` still open in the tab)
      // is torn down before we start a new one. `startRegistration` /
      // `startAuthentication` will install a fresh abort signal internally.
      try {
        WebAuthnAbortService.cancelCeremony();
      } catch {
        // Best-effort.
      }

      const response = await trySafe(
        async () =>
          withClientTimeout<RegistrationResponseJSON | AuthenticationResponseJSON>(async () =>
            parsedRegistrationOptions.success
              ? startRegistration(parsedRegistrationOptions.data)
              : startAuthentication(options)
          ),
        (error: unknown) => {
          // Preserve existing user-cancel semantics (Safari/Chrome cancel = NotAllowedError).
          if (
            (error instanceof DOMException && error.name === 'NotAllowedError') ||
            (error instanceof Error && error.name === 'NotAllowedError')
          ) {
            return;
          }
          // AbortError arrives here after our own client-timeout cancel. The
          // timeout branch already surfaces a retry-friendly failure, so
          // suppress the duplicate "failed to create/verify" toast.
          if (
            (error instanceof DOMException && error.name === 'AbortError') ||
            (error instanceof Error && error.name === 'AbortError')
          ) {
            return;
          }
          setToast(
            t(
              parsedRegistrationOptions.success
                ? 'mfa.webauthn_failed_to_create'
                : 'mfa.webauthn_failed_to_verify'
            )
          );
        }
      );

      if (!response) {
        return;
      }

      /**
       * Assert type manually to get the correct type
       */
      await sendMfaPayload(
        isAuthenticationResponseJSON(response)
          ? {
              flow: UserMfaFlow.MfaVerification,
              payload: { ...response, type: MfaFactor.WebAuthn },
              verificationId,
            }
          : {
              flow: UserMfaFlow.MfaBinding,
              payload: { ...response, type: MfaFactor.WebAuthn },
              verificationId,
            }
      );
    },
    [sendMfaPayload, setToast, t]
  );
};

export default useWebAuthnOperation;
